import { Fragment, useEffect, useState } from 'react'
import { Dropdown, DropdownToggle, DropdownItem } from 'reactstrap'
import { mutations } from '../../../../../../api'
import { getChatFriendContent } from '../../../../../../data/messenger'
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks'
import { SvgIcon } from '../../../../../../shared/icons'
import ConfirmModal from '../../../../../../shared/modal/ConfirmationModal'
import { getChatId } from '../../../../../../utils'
import { useMessageSelection } from '../../chat/message-action/useMessageSelection'
import MuteChatModal from './MuteChatModal'
import { setProfileToggle } from '../../../../../../redux/reducers/messenger/messengerSlice'
import {
  selectChat,
  setOpenReportModal,
  setSelectedUser,
  removeRecentChat,
  setUserDeleted,
  setViewProfile,
} from '../../../../../../redux/reducers/messenger/chatSlice'
import ReportUserModal from '../../../../left-sidebar-chat/profile-section/ReportUserModal'
import DisappearingModal from './DisappearingModal'
import LockChatModal from './LockChatModal'
import { useTranslation } from 'react-i18next'
import DropdownWrapper from '../../../../../../shared/DropdownWrapper'
import { getEventPath, getShadowRoot } from '../../../../../../Webcomponent/utils/common'
import { hideOtherFeature } from '../../../../../../constants'
import { ChatMembersType } from '../../../../../../types/store'

const QuickAction = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { isChatList } = useAppSelector((state) => state.templateCustomizer)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const { toggleSmallSide, profileToggle } = useAppSelector((state) => state.messenger)
  const { selectedUser, openReportModal } = useAppSelector((state) => state.chat)
  const [showClearModal, setShowClearModal] = useState(false)
  const [isMuteModalOpen, setIsMuteModalOpen] = useState(false)
  const [disappearingOpen, setDisappearingOpen] = useState(false)
  const [lockOpen, setLockOpen] = useState(false)
  const { mutate } = mutations.useClearChat()
  const id = getChatId(selectedUser)
  const { isSelectionMode, clearSelection, enterEmptySelectionMode } = useMessageSelection()
  const isGroupChat = selectedUser?.chat_type === 'group'
  const { mutate: deleteChat, isPending: isDeleting } = mutations.useDeleteChat()
  const { mutate: deleteBroadcast, isPending: isDeletingBroadcast } = mutations.useDeleteBroadcast()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteBroadcast, setShowDeleteBroadcast] = useState(false)

  useEffect(() => {
    const shadowRoot = getShadowRoot()
    const meetzyMain = shadowRoot?.querySelector('.meetzy-main')
    const appSidebar = shadowRoot?.querySelector('.app-sidebar')
    if (typeof document !== 'undefined') {
      if (toggleSmallSide) {
        meetzyMain?.classList.add('small-sidebar')
        appSidebar?.classList.add('active')
        document.body.classList.add('sidebar-active', 'main-page')
      } else {
        meetzyMain?.classList.remove('small-sidebar')
        appSidebar?.classList.remove('active')
        document.body.classList.remove('sidebar-active', 'main-page')
      }
    }
  }, [toggleSmallSide])

  const handleClear = () => {
    mutate({ id: id, type: selectedUser?.chat_type, broadcastId: selectedUser?.chat_id })
    handleClearCancel()
  }

  const handleClearClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowClearModal(true)
  }

  const handleClearCancel = () => {
    setShowClearModal(false)
  }

  const handleToggleSelectionMode = () => {
    if (isSelectionMode) {
      clearSelection()
    } else {
      enterEmptySelectionMode()
    }
  }

  const handleContactClick = () => {
    if (!isGroupChat) {
      dispatch(setProfileToggle(!profileToggle))
    }
  }

  const handleViewProfile = () => {
    dispatch(setViewProfile(selectedUser as ChatMembersType))
  }

  const handleCloseClick = () => {
    dispatch(setSelectedUser(null))
    dispatch(selectChat(null))
  }

  const handleReportClick = () => {
    setDropdownOpen(false)
    dispatch(setOpenReportModal())
  }

  const confirmDelete = () => {
    const targetId = selectedUser?.chat_id
    const apiTargetType = selectedUser?.chat_type === 'group' ? 'group' : 'user'
    const reduxChatType = selectedUser?.chat_type === 'group' ? 'group' : 'direct'

    deleteChat(
      { targetId, targetType: apiTargetType },
      {
        onSuccess: () => {
          setShowDeleteModal(false)
          dispatch(setSelectedUser(null))
          dispatch(setUserDeleted(selectedUser?.chat_id!))
          dispatch(selectChat(null))
          if (targetId) {
            dispatch(removeRecentChat({ chatId: targetId, chatType: reduxChatType }))
          }
        },
      },
    )
  }

  const confirmDeleteBroadcast = () => {
    const targetId = selectedUser?.chat_id

    deleteBroadcast(
      { broadcast_id: targetId },
      {
        onSuccess: () => {
          setShowDeleteBroadcast(false)
          dispatch(setSelectedUser(null))
          dispatch(selectChat(null))
        },
      },
    )
  }

  const handleDeleteClick = () => {
    setShowDeleteModal(true)
  }

  const handleDeleteBroadcastClick = () => {
    setShowDeleteBroadcast(true)
  }

  const handleDisappearingClick = () => {
    setDisappearingOpen(true)
  }

  const handleLockClick = () => {
    setLockOpen(true)
  }

  const hiddenTitles = new Set<string>()

  if (hideOtherFeature.hideDisappearing) {
    hiddenTitles.add('disappearing_messages')
  }

  if (hideOtherFeature.hideLockChat) {
    hiddenTitles.add('unlock_chat')
    hiddenTitles.add('lock_chat')
  }

  if (hideOtherFeature.hideCloseChat) {
    hiddenTitles.add('close_chat')
  }

  if (hideOtherFeature.hideReportChat) {
    hiddenTitles.add('report')
  }

  let chatFriendContent = getChatFriendContent(selectedUser, {
    handleClearClick,
    handleContactClick,
    handleViewProfile,
    handleToggleSelectionMode,
    handleCloseClick,
    handleReportClick,
    handleDeleteClick,
    handleDeleteBroadcastClick,
    handleDisappearingClick,
    handleLockClick,
  }).filter((item) => !hiddenTitles.has(item.title))

  if (isChatList) {
    chatFriendContent = chatFriendContent.filter((item) => {
      if (
        item.title === 'contact_info' ||
        item.title === 'lock_chat' ||
        item.title === 'unlock_chat' ||
        item.title === 'close_chat' ||
        item.title === 'delete_broadcast'
      ) {
        return false
      }
      return true
    })
  }

  // Announcement/Admin chats are read-only, so most actions don't apply. Keep
  // only Contact Info and Select Message.
  if (selectedUser?.isAnnouncement) {
    chatFriendContent = chatFriendContent.filter(
      (item) => item.title === 'contact_info' || item.title === 'select_message',
    )
  }

  const toggleDropdown = (e?: React.MouseEvent) => {
    if (!e) {
      setDropdownOpen((v) => !v)
      return
    }
    const nativeEvent = e.nativeEvent as any
    const path = getEventPath(nativeEvent)
    // ✅ ONLY toggle if click originated from DropdownToggle
    if (path && path.length > 0) {
      const isToggleClick = path.some(
        (el) =>
          el instanceof HTMLElement &&
          (el.classList.contains('dropdown_toggle') || el.classList.contains('dropdown-menu-custom')),
      )
      if (isToggleClick) {
        setDropdownOpen((v) => !v)
      }
    } else {
      setTimeout(() => {
        setDropdownOpen(false)
      }, 150)
    }
  }

  return (
    <Fragment>
      <li className="icon-btn chat-friend-toggle">
        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
          <DropdownToggle className="icon-btn rounded15 button-effect dropdown_toggle" tag="button" color="">
            <SvgIcon iconId="header-toggle" />
          </DropdownToggle>
          <DropdownWrapper end className="dropdown-menu-custom ds">
            {chatFriendContent
              .filter((item) => item.allow)
              .map((item, index) => (
                <Fragment key={index}>
                  <DropdownItem
                    onClick={(e) => {
                      item.onClick(e as any)
                    }}
                    className="d-flex align-items-center"
                  >
                    <SvgIcon className={item.class} iconId={item.icon} />
                    <h5 className="ms-2 mb-0">{t(item.title)}</h5>
                  </DropdownItem>
                  {item.title == 'Close Chat' && <DropdownItem divider className="mx-2 border-bottom-0" />}
                </Fragment>
              ))}
          </DropdownWrapper>
        </Dropdown>
      </li>
      <ConfirmModal
        isOpen={showClearModal}
        onClose={handleClearCancel}
        onConfirm={handleClear}
        title="Clear Chat Confirmation"
        subtitle="Are you sure you want to clear your chat?"
        confirmText="Clear Chat"
        cancelText="Cancel"
        variant="info"
        iconId="confirmation"
      />
      <MuteChatModal isOpen={isMuteModalOpen} onClose={() => setIsMuteModalOpen(false)} />
      <DisappearingModal isOpen={disappearingOpen} onClose={() => setDisappearingOpen(false)} />
      <LockChatModal isOpen={lockOpen} onClose={() => setLockOpen(false)} />
      {!hideOtherFeature.hideReportChat && (
        <ReportUserModal open={openReportModal || false} toggleModal={handleReportClick} />
      )}

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete Chat"
        subtitle="Are you sure you want to delete this chats?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmModal
        isOpen={showDeleteBroadcast}
        onClose={() => setShowDeleteBroadcast(false)}
        onConfirm={confirmDeleteBroadcast}
        isLoading={isDeletingBroadcast}
        title="Delete Broadcast"
        subtitle="Are you sure you want to delete this broadcast?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </Fragment>
  )
}

export default QuickAction
